import React, { useState, useEffect, useCallback } from "react";
import {
  Switch,
  Card,
  Typography,
  Space,
  message,
  Spin,
  Alert,
  Divider,
  Button,
  Tag,
} from "antd";
import { supabase } from "../lib/supabase";

const { Text } = Typography;

interface EmailPreference {
  status: string;
  enabled: boolean;
  notification_type?: string;
}

interface FieldPreference {
  notification_type: string;
  enabled: boolean;
}

// Statuses that are always handled by n8n (cannot be disabled)
const n8nHandledStatuses = ["submitted", "approved", "rejected"];

// Statuses that are excluded from email notifications entirely
const excludedStatuses = ["concept", "archived"];

interface AdminUserEmailPreferencesProps {
  userId: string;
}

export const AdminUserEmailPreferences: React.FC<
  AdminUserEmailPreferencesProps
> = ({ userId }) => {
  const [preferences, setPreferences] = useState<EmailPreference[]>([]);
  const [fieldPreferences, setFieldPreferences] = useState<FieldPreference[]>(
    []
  );
  const [globalEnabled, setGlobalEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [updatingGlobal, setUpdatingGlobal] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("user_email_notification_preferences")
        .select("*")
        .eq("user_id", userId);

      if (error) throw error;

      // Get global preference
      const globalPref = data?.find(
        (p) => p.notification_type === "global" && p.status === "global"
      );
      setGlobalEnabled(globalPref?.global_enabled ?? false);

      const fieldTypes = [
        {
          type: "expected_delivery_date_change",
          defaultEnabled: false,
        },
        {
          type: "container_operational_eta_change",
          defaultEnabled: false,
        },
        {
          type: "container_etd_change",
          defaultEnabled: false,
        },
      ];
      const fieldPrefs = fieldTypes.map(({ type, defaultEnabled }) => {
        const existing = data?.find(
          (p) => p.notification_type === type && p.status === type
        );
        return {
          notification_type: type,
          enabled: existing?.enabled ?? defaultEnabled,
        };
      });
      setFieldPreferences(fieldPrefs);

      // Create preferences for all statuses, excluding concept and archived
      const allStatuses = [
        "waiting_for_goods",
        "goods_received_origin",
        "in_transit",
        "customs_pending_clearance",
        "customs_cleared",
        "fyco",
        "delivery_planned",
        "out_for_delivery",
        "delivered",
        "added_to_stock",
        "cancelled",
      ];
      // Statuses that are enabled by default (but can be disabled)
      const defaultEnabledStatuses = ["fyco", "delivery_planned"];

      const userPreferences = allStatuses.map((status) => {
        const existing = data?.find(
          (p) =>
            p.status === status &&
            (p.notification_type === "status_change" || !p.notification_type)
        );
        return {
          status,
          // For 'fyco' and 'delivery_planned': enabled by default (NULL = enabled), unless explicitly disabled
          // For other statuses: default to disabled (opt-in)
          enabled: defaultEnabledStatuses.includes(status)
            ? existing?.enabled ?? true // NULL or true = enabled, false = disabled
            : existing?.enabled ?? false,
        };
      });

      setPreferences(userPreferences);
    } catch (error) {
      console.error("Error fetching preferences:", error);
      message.error("Failed to load email preferences");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updateGlobalPreference = async (enabled: boolean) => {
    setUpdatingGlobal(true);
    try {
      const { error } = await supabase
        .from("user_email_notification_preferences")
        .upsert(
          {
            user_id: userId,
            status: "global",
            notification_type: "global",
            global_enabled: enabled,
            enabled: true,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        );

      if (error) throw error;

      setGlobalEnabled(enabled);
      message.success("Global preference updated successfully");
    } catch (error) {
      console.error("Error updating global preference:", error);
      message.error("Failed to update global preference");
    } finally {
      setUpdatingGlobal(false);
    }
  };

  const updateFieldPreference = async (
    notificationType: string,
    enabled: boolean
  ) => {
    setUpdating(notificationType);
    try {
      const { error } = await supabase
        .from("user_email_notification_preferences")
        .upsert(
          {
            user_id: userId,
            status: notificationType,
            notification_type: notificationType,
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        );

      if (error) throw error;

      setFieldPreferences((prev) =>
        prev.map((p) =>
          p.notification_type === notificationType ? { ...p, enabled } : p
        )
      );

      message.success("Preference updated successfully");
    } catch (error) {
      console.error("Error updating field preference:", error);
      message.error("Failed to update preference");
    } finally {
      setUpdating(null);
    }
  };

  const updatePreference = async (status: string, enabled: boolean) => {
    setUpdating(status);
    try {
      const { error } = await supabase
        .from("user_email_notification_preferences")
        .upsert(
          {
            user_id: userId,
            status,
            notification_type: "status_change",
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        );

      if (error) throw error;

      setPreferences((prev) =>
        prev.map((p) => (p.status === status ? { ...p, enabled } : p))
      );

      message.success("Preference updated successfully");
    } catch (error) {
      console.error("Error updating preference:", error);
      message.error("Failed to update preference");
    } finally {
      setUpdating(null);
    }
  };

  const updateAllFieldPreferences = async (enabled: boolean) => {
    if (!globalEnabled) {
      message.warning("Please enable global notifications first");
      return;
    }

    setUpdating("all_fields");
    try {
      const updates = fieldPreferences.map((pref) =>
        supabase.from("user_email_notification_preferences").upsert(
          {
            user_id: userId,
            status: pref.notification_type,
            notification_type: pref.notification_type,
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        )
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw errors[0].error;

      setFieldPreferences((prev) => prev.map((p) => ({ ...p, enabled })));

      message.success(
        enabled ? "All field updates enabled" : "All field updates disabled"
      );
    } catch (error) {
      console.error("Error updating all field preferences:", error);
      message.error("Failed to update preferences");
    } finally {
      setUpdating(null);
    }
  };

  const updateAllStatusPreferences = async (enabled: boolean) => {
    if (!globalEnabled) {
      message.warning("Please enable global notifications first");
      return;
    }

    setUpdating("all_statuses");
    try {
      // Filter out n8n-handled statuses (but allow default-enabled statuses to be toggled)
      const updatableStatuses = preferences.filter(
        (p) => !n8nHandledStatuses.includes(p.status)
      );

      const updates = updatableStatuses.map((pref) =>
        supabase.from("user_email_notification_preferences").upsert(
          {
            user_id: userId,
            status: pref.status,
            notification_type: "status_change",
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        )
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw errors[0].error;

      setPreferences((prev) =>
        prev.map((p) =>
          n8nHandledStatuses.includes(p.status) ? p : { ...p, enabled }
        )
      );

      message.success(
        enabled ? "All status updates enabled" : "All status updates disabled"
      );
    } catch (error) {
      console.error("Error updating all status preferences:", error);
      message.error("Failed to update preferences");
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>Loading preferences...</div>
      </div>
    );
  }

  const statusLabels: Record<string, string> = {
    waiting_for_goods: "Waiting for Goods",
    goods_received_origin: "Goods Received at Origin",
    in_transit: "In Transit",
    customs_pending_clearance: "Customs Pending Clearance",
    customs_cleared: "Customs Cleared",
    fyco: "FYCO",
    delivery_planned: "Delivery Planned",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    added_to_stock: "Added to Stock",
    cancelled: "Cancelled",
  };

  const fieldLabels: Record<string, string> = {
    expected_delivery_date_change: "Expected delivery date updates",
    container_operational_eta_change: "Container ETA (operational) updates",
    container_etd_change: "Container ETD updates",
  };

  return (
    <div style={{ padding: "16px 0" }}>
      <Alert
        message="Email Notification Preferences"
        description="Manage which email notifications this user will receive. Global toggle must be enabled for any notifications to be sent."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Global Toggle */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Text strong>Global Email Notifications</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Master switch for all email notifications
              </Text>
            </div>
            <Switch
              checked={globalEnabled}
              onChange={updateGlobalPreference}
              loading={updatingGlobal}
              disabled={updatingGlobal}
            />
          </div>
        </Space>
      </Card>

      {/* Field-based Preferences */}
      {fieldPreferences.length > 0 && (
        <Card
          size="small"
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Field Updates</span>
              <Button
                size="small"
                onClick={() => updateAllFieldPreferences(true)}
                disabled={!globalEnabled || updating === "all_fields"}
                style={{ marginRight: 8 }}
              >
                Enable All
              </Button>
              <Button
                size="small"
                onClick={() => updateAllFieldPreferences(false)}
                disabled={!globalEnabled || updating === "all_fields"}
              >
                Disable All
              </Button>
            </div>
          }
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {fieldPreferences.map((pref) => (
              <div
                key={pref.notification_type}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text>
                  {fieldLabels[pref.notification_type] ||
                    pref.notification_type}
                </Text>
                <Switch
                  checked={pref.enabled && globalEnabled}
                  onChange={(checked) =>
                    updateFieldPreference(pref.notification_type, checked)
                  }
                  loading={updating === pref.notification_type}
                  disabled={
                    !globalEnabled || updating === pref.notification_type
                  }
                />
              </div>
            ))}
          </Space>
        </Card>
      )}

      {/* Status-based Preferences */}
      <Card
        size="small"
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Status Change Notifications</span>
            <Button
              size="small"
              onClick={() => updateAllStatusPreferences(true)}
              disabled={!globalEnabled || updating === "all_statuses"}
              style={{ marginRight: 8 }}
            >
              Enable All
            </Button>
            <Button
              size="small"
              onClick={() => updateAllStatusPreferences(false)}
              disabled={!globalEnabled || updating === "all_statuses"}
            >
              Disable All
            </Button>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          {preferences.map((pref) => {
            const isN8nHandled = n8nHandledStatuses.includes(pref.status);
            const isDefaultEnabled = ["fyco", "delivery_planned"].includes(
              pref.status
            );
            return (
              <div
                key={pref.status}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  opacity: isN8nHandled ? 0.6 : 1,
                }}
              >
                <div>
                  <Text>
                    {statusLabels[pref.status] || pref.status}
                    {isN8nHandled && (
                      <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>
                        n8n
                      </Tag>
                    )}
                    {isDefaultEnabled && !isN8nHandled && (
                      <Tag
                        color="green"
                        style={{ marginLeft: 8, fontSize: 10 }}
                      >
                        Enabled by default
                      </Tag>
                    )}
                  </Text>
                </div>
                <Switch
                  checked={
                    // For default-enabled statuses: bypass global toggle, show actual preference state
                    // For others: require global toggle AND preference
                    isDefaultEnabled
                      ? pref.enabled
                      : pref.enabled && globalEnabled
                  }
                  onChange={(checked) => updatePreference(pref.status, checked)}
                  loading={updating === pref.status}
                  disabled={
                    !globalEnabled || isN8nHandled || updating === pref.status
                  }
                />
              </div>
            );
          })}
        </Space>
        <Divider style={{ margin: "12px 0" }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Note: Statuses marked with "n8n" are handled by the existing n8n
          workflow and cannot be disabled here.
        </Text>
      </Card>
    </div>
  );
};
